import { type PageProps, graphql } from "gatsby";
import React, { useEffect } from "react";

import { ArticleMidgate } from "../../components/ArticleMidgate";
import Layout from "../../components/Layout";
import { SEO } from "../../components/SEO";
import { stripHtml } from "../../lib/stringUtils";
import type { BreadCrumbs } from "../../types/breadcrumbs";

interface Props extends PageProps {
	data: Queries.ArticleMidgateQuery;
	pageContext: {
		id: string;
		breadcrumb: BreadCrumbs;
		pagePath: string;
	};
}

export const ArticleMidgateTemplate: React.FC<Props> = ({
	data,
	pageContext,
	location,
}) => {

	if (!data?.article) {
		console.log(`No data found for article: ${pageContext.path}`, data);
		return <div />;
	}
	const title = data.article?.title || "RUSI";
	const abstract = data.article?.field_abstract?.value;
	const breadcrumb = pageContext.breadcrumb?.crumbs;

	// Metatags
	const metaDescription = abstract && stripHtml(abstract);
	const signpostImage =
		data.article?.relationships?.field_signpost_image?.relationships
			?.field_media_image?.childImageKitAsset?.fixed?.images?.fallback?.src ||
		undefined;

	const bannerImage =
		data.article?.relationships?.field_media_image?.relationships
			?.field_media_image?.childImageKitAsset?.fluid?.images?.fallback?.src ||
		undefined;

	const topics = data.article.relationships?.field_topics || [];
	const interestAreas = data.article.relationships?.field_interest_areas || [];
	const researchGroups =
		data.article.relationships?.field_research_groups || [];
	const networks = data.article.relationships?.field_networks || [];
	const regions = data.article.relationships?.field_region || [];
	const projects = data.article.relationships?.field_related_project || [];
	const author = data.article.relationships?.field_author || [];

    useEffect(() => {
        const dataLayerObject = {
            topics: topics.map((topic) => topic.name),
            researchGroups: researchGroups.map((researchGroup) => researchGroup.name),
            region: regions.map((region) => region.name),
            networks: networks.map((network) => network.name),
            interestAreas: interestAreas.map((interest) => interest.name),
            projects: projects?.map((project) => project.name),
            authors: author?.map(
                (author) => `${author.field_first_names} ${author.title}`,
            ),
            publishedDate: data.article.created,
            event: "dataLayerArticlePush",
            _clear: true,
        };

        if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(dataLayerObject);

            window._mtm = window._mtm || [];
            window._mtm.push({
                event: 'dataLayerArticlePushMatomo',
                pagePath: location.pathname,
                authors: dataLayerObject.authors,
                topics: dataLayerObject.topics,
                region: dataLayerObject.region,
                researchGroups: dataLayerObject.researchGroups,
                networks: dataLayerObject.networks,
                interestAreas: dataLayerObject.interestAreas,
                projects: dataLayerObject.projects,
                publishedDate: dataLayerObject.publishedDate,
            });
        }
    }, []);

    return (
		<Layout>
			{data.article && (
				<>
					<SEO
						title={title}
						image={signpostImage || bannerImage}
						description={metaDescription}
						url={data.article?.path?.alias}
					/>
					<ArticleMidgate
						article={data.article}
						url={location.href}
						breadcrumb={breadcrumb}
						midgateAccount={data.midgateAccount}
					/>
				</>
			)}
		</Layout>
	);
};

export default ArticleMidgateTemplate;

export const fragment = graphql`
    fragment ArticleMidgateData on node__article_midgate {
        __typename
        id
        path {
            alias
        }
        field_read_time
        title
        created: field_authored_date(formatString: "D MMMM Y")
        field_outside_author
        field_profile_descriptors
        field_premium
        field_abstract {
            value
            format
            processed
        }
        previewContent {
            ...FieldSections
        }

        relationships {
            field_media_enquiry_ {
                ...NodeMediaEnquiries
            }
            field_open_content {
                    ...FieldSections
            }
#            field_content_type {
#                name
#                path {
#                    alias
#                }
#            }
            field_author {
                ...PersonData
            }
            # tags
            field_region {
                id
                name
                path {
                    alias
                }
            }
            field_research_groups {
                id
                name
                path {
                    alias
                }
            }
            field_topics {
                id
                name
                path {
                    alias
                }
            }
            field_related_project {
                id
                name
                path {
                    alias
                }
            }

            field_networks {
                id
                name
                path {
                    alias
                }
            }

            field_interest_areas {
                id
                name
                path {
                    alias
                }
            }

            field_media_image {
                relationships {
                    field_media_image {
                        childImageKitAsset {
                            fluid(maxWidth: 1168) {
                                ...ImageKitFluid
                            }
                        }
                        relationships {
                            media__image {
                                field_credit
                                field_credit_link
                                field_media_image {
                                    alt
                                }
                            }
                        }
                    }
                }
            }

            field_signpost_image {
                relationships {
                    field_media_image {
                        childImageKitAsset {
                            fixed(width: 1200, transformations: ["h-630"]) {
                                ...ImageKitFixed
                            }
                        }
                    }
                }
            }
					
            field_footnotes {
                ...FieldFootnote
            }
        }

        # Encrypted data
        fields {
            key
            encrypted_validate {
                ...EncryptedNode
            }
            encrypted_body {
                ...EncryptedNode
            }
            encrypted_taylor_and_francis_url {
                ...EncryptedNode
            }
        }
        encryptedSections {
            ...EncryptedNode
        }
        encryptedPdfs {
            ...EncryptedNode
        }
        openDownloads {
            ...EncryptedNode
        }
        
    }
		
		
`;
export const pageQuery = graphql`
     query ArticleMidgate($path: String!) {
         article: nodeArticleMidgate(path: { alias: { eq: $path } }) {
             ...ArticleMidgateData
         },
				 midgateAccount: configPagesGatedContentMessagingMidGate {
						 field_heading
						 field_register_button_label
						 field_login_button_label
						 field_subtext
						 field_tick_icon_labels
						 field_continue_button_label
				 }	
     }
`;
